// src/components/agent/parts/ImagesBlock.js
//
// PersistedImagePart, extracted verbatim from MessageDisplay.js (frontend
// spec §8.6): signed-URL image previews, PDF page captions, truncation note.
// Renders inside the primary-coloured user bubble, so the v1 white-alpha
// styling carries over.
//
// part: { filename, fileKind, image_assets: [{ gcs_path, page_number }],
//         total_page_count, gcsPath (the original upload, if known) }
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Stack, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getSignedUrl } from '../../../services/api_utils';

// The previews a PDF part carries are the pages the model was shown; a
// document can have fewer previews than pages (20 per document, and 20
// images per message across every attachment), so the note compares the
// two rather than assuming a fixed cap.
const omittedPagesNote = (part) => {
  if (part?.fileKind !== 'pdf') return null;
  const totalPages = Number(part.total_page_count || 0);
  const previewedPages = (part.image_assets || []).length;
  if (totalPages <= previewedPages) return null;
  return `Showing ${previewedPages} of ${totalPages} pages; the full PDF opens from the file.`;
};

function ImagesBlock({ part, onOpenFile }) {
  const [resolvedAssets, setResolvedAssets] = useState([]);
  const [loading, setLoading] = useState(Boolean(part?.image_assets?.length));
  const [error, setError] = useState(null);
  const resolvedAssetsRef = useRef(resolvedAssets);
  const assetSignature = useMemo(
    () => JSON.stringify(
      (part?.image_assets || []).map((asset) => ({
        gcs_path: asset.gcs_path,
        page_number: asset.page_number || null,
      }))
    ),
    [part?.image_assets]
  );

  useEffect(() => {
    resolvedAssetsRef.current = resolvedAssets;
  }, [resolvedAssets]);

  useEffect(() => {
    let cancelled = false;

    const loadAssets = async () => {
      if (!part?.image_assets?.length) {
        setResolvedAssets([]);
        setLoading(false);
        return;
      }

      try {
        if (!resolvedAssetsRef.current.length) {
          setLoading(true);
        }
        setError(null);
        const nextAssets = await Promise.all(
          part.image_assets.map(async (asset) => ({
            ...asset,
            url: await getSignedUrl(asset.gcs_path),
          }))
        );
        if (!cancelled) {
          setResolvedAssets(nextAssets);
        }
      } catch (err) {
        console.error('Failed to load persisted image attachment:', err);
        if (!cancelled && !resolvedAssetsRef.current.length) {
          setError('Failed to load attachment preview.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAssets();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetSignature]);

  if (!part?.image_assets?.length) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {part.filename}
        </Typography>
        {/* The original file (not the rendered pages) opens in the file pane,
            where it can also be downloaded. */}
        {part.gcsPath && onOpenFile && (
          <Tooltip title="Open original">
            <IconButton
              size="small"
              onClick={() => onOpenFile({ filename: part.filename, downloadUrl: null, gcsPath: part.gcsPath, sandboxPath: part.sandboxPath || null, toolUseId: null })}
              sx={{ color: 'inherit', p: 0.25 }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {omittedPagesNote(part) && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {omittedPagesNote(part)}
        </Typography>
      )}
      {loading && !resolvedAssets.length && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} sx={{ color: 'inherit' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Loading attachment preview...
          </Typography>
        </Box>
      )}
      {error && !resolvedAssets.length && (
        <Typography variant="caption" color="error.light">
          {error}
        </Typography>
      )}
      {resolvedAssets.map((asset, index) => (
        <Box
          key={`${asset.gcs_path || 'asset'}-${index}`}
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.16)',
          }}
        >
          <Box
            component="img"
            src={asset.url}
            alt={part.fileKind === 'pdf' && asset.page_number ? `${part.filename} page ${asset.page_number}` : part.filename || 'Attachment'}
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: 360,
              objectFit: 'contain',
              backgroundColor: 'rgba(255,255,255,0.04)',
            }}
          />
          {part.fileKind === 'pdf' && asset.page_number && (
            <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, color: 'rgba(255,255,255,0.8)' }}>
              Page {asset.page_number}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

export default ImagesBlock;
